/**
 * React Query hooks — Job Openings.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	RecruitmentApiService,
	type JobOpeningFilters,
	type JobOpeningCreate,
	type JobOpeningUpdate,
} from '../../../services/recruitmentApi';

const KEY = ['recruitment', 'jobs'] as const;

export const useJobs = (filters: JobOpeningFilters = {}) =>
	useQuery({
		queryKey: [...KEY, 'list', filters],
		queryFn: async () => {
			const resp = await RecruitmentApiService.listJobs(filters);
			return resp.data; // { page, page_size, total, rows }
		},
	});

export const useJob = (id: number | undefined) =>
	useQuery({
		queryKey: [...KEY, 'detail', id],
		enabled: typeof id === 'number' && !Number.isNaN(id),
		queryFn: async () => {
			const resp = await RecruitmentApiService.getJob(id as number);
			return resp.data;
		},
	});

export const useCreateJob = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: JobOpeningCreate) => {
			const resp = await RecruitmentApiService.createJob(data);
			return resp.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: KEY });
		},
	});
};

export const useUpdateJob = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, data }: { id: number; data: JobOpeningUpdate }) => {
			const resp = await RecruitmentApiService.updateJob(id, data);
			return resp.data;
		},
		onSuccess: (_data, variables) => {
			qc.invalidateQueries({ queryKey: KEY });
			qc.invalidateQueries({ queryKey: [...KEY, 'detail', variables.id] });
		},
	});
};
